import { useState } from 'react';
import { T, Var } from 'gt-react';
import { DownloadSimpleIcon } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FlaggedDownloadFile } from '@/lib/daily-reports/flaggedDownloads';
import { sanitizeChosenDownloadFileName } from '@/lib/daily-reports/suggestedDownloadFileName';

export function DownloadFlaggedRowsDialog(props: {
  files: FlaggedDownloadFile[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (files: FlaggedDownloadFile[]) => void;
}) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {props.open ? (
          <DownloadFlaggedRowsForm
            files={props.files}
            onOpenChange={props.onOpenChange}
            onConfirm={props.onConfirm}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DownloadFlaggedRowsForm({
  files,
  onOpenChange,
  onConfirm,
}: {
  files: FlaggedDownloadFile[];
  onOpenChange: (open: boolean) => void;
  onConfirm: (files: FlaggedDownloadFile[]) => void;
}) {
  const [names, setNames] = useState(() => files.map((file) => file.fileName));
  const namedFiles = files.map((file, index) => ({
    ...file,
    fileName: sanitizeChosenDownloadFileName(names[index] ?? ''),
  }));
  const canDownload = namedFiles.every((file) => file.fileName.length > 0);

  function confirmDownload(): void {
    if (!canDownload) return;
    onConfirm(namedFiles);
    onOpenChange(false);
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        confirmDownload();
      }}
    >
      <DialogHeader>
        <DialogTitle>
          <T context="title of the daily reports download filename dialog">
            Rename Download
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="subtitle of the daily reports download filename dialog">
            Accept the suggested name or change it before downloading.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3 py-2">
        {files.map((file, index) => (
          <div key={file.fileName} className="flex flex-col gap-1.5">
            <Label htmlFor={`daily-report-download-name-${index}`}>
              {files.length === 1 ? (
                <T context="label for the daily reports download filename field">
                  File Name
                </T>
              ) : (
                <T context="label for one of several daily reports download filename fields">
                  File Name <Var>{index + 1}</Var>
                </T>
              )}
            </Label>
            <Input
              id={`daily-report-download-name-${index}`}
              autoFocus={index === 0}
              value={names[index] ?? ''}
              onChange={(event) => {
                const next = [...names];
                next[index] = event.target.value;
                setNames(next);
              }}
            />
          </div>
        ))}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
        >
          <T context="cancel button in the daily reports download filename dialog">
            Cancel
          </T>
        </Button>
        <Button type="submit" disabled={!canDownload}>
          <DownloadSimpleIcon className="h-4 w-4" />
          <T context="confirm button that downloads daily report CSVs with the chosen names">
            Download
          </T>
        </Button>
      </DialogFooter>
    </form>
  );
}
